"use client";

import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import {
  selectIsAuthSuccessOrPerpetual,
  selectLeadStatus,
  useAppSelector,
} from "@repo/web-store";
import { useRouter, useSearchParams } from "next/navigation";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { getBudgetFriendlyETFs } from "../../services/TradingMallService";
import { StockItem } from "../../types/stocks";
import StockListNotFound from "../ClpStockCards/StockListNotFound";
import { handleBudgetEtfCardClick } from "./BudgetEtfActionHandlers";
import BudgetEtfHeader from "./BudgetEtfHeader";
import BudgetEtfListRow from "./BudgetEtfListRow";
import { trackBudgetEtfClick } from "./budgetEtfTracking";
import {
  BUDGET_ETF_CLICK_TYPE_CARD,
  BUDGET_ETF_CLICK_TYPE_ICON,
  BUDGET_ETF_CLICK_TYPE_LINK,
  BUDGET_ETF_CLICK_TYPE_TAB,
} from "./budgetEtfTrackingConstants";
import {
  BudgetEtfSortMode,
  getNextSortMode,
  mapResponseToStocks,
  resolveLtpFilter,
  resolveSortForApi,
} from "./budgetEtfUtils";
import {
  BUDGET_ETF_COLORS,
  BUDGET_ETF_FILTERS,
  BUDGET_ETF_ICONS,
  BUDGET_ETF_MAIN_AEM_DEFAULTS,
  BudgetETFMainClientProps,
  LTP_FILTER_QUERY_PARAM,
} from "./BudgetETFTypes";

const PAGE_LIMIT = 10;
const SKELETON_ROW_KEYS = [
  "budget-etf-skeleton-first",
  "budget-etf-skeleton-second",
  "budget-etf-skeleton-third",
];

const BudgetETFMainClient: FC<BudgetETFMainClientProps> = ({
  initialStocks,
  initialShowMore = false,
  initialShowLess = false,
  initialLtpFilter,
  defaultSort,
  heading = BUDGET_ETF_MAIN_AEM_DEFAULTS.heading,
  description = BUDGET_ETF_MAIN_AEM_DEFAULTS.description,
  loggedInRedirectionUrl = BUDGET_ETF_MAIN_AEM_DEFAULTS.loggedInRedirectionUrl,
  screen = BUDGET_ETF_MAIN_AEM_DEFAULTS.screen,
  webViewTitle,
  componentPosition,
  stockNotFoundText = BUDGET_ETF_MAIN_AEM_DEFAULTS.stockNotFoundText,
  stockNotFoundImgUrl = BUDGET_ETF_MAIN_AEM_DEFAULTS.stockNotFoundImgUrl,
  showMoreText = BUDGET_ETF_MAIN_AEM_DEFAULTS.showMoreText,
  showLessText = BUDGET_ETF_MAIN_AEM_DEFAULTS.showLessText,
  companyName = BUDGET_ETF_MAIN_AEM_DEFAULTS.companyName,
  mainHeading = BUDGET_ETF_MAIN_AEM_DEFAULTS.mainHeading,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isLoggedIn = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const leadStatus = useAppSelector(selectLeadStatus);
  const isEtbUser = leadStatus === "ETB";
  const isRowClickable = isLoggedIn && isEtbUser;

  const [activeFilter, setActiveFilter] = useState(
    resolveLtpFilter(initialLtpFilter),
  );
  const [sortMode, setSortMode] = useState<BudgetEtfSortMode>("default");
  const [stocks, setStocks] = useState<StockItem[]>(initialStocks);
  const [page, setPage] = useState(1);
  const [showMore, setShowMore] = useState(initialShowMore);
  const [showLess, setShowLess] = useState(initialShowLess);
  const [isLoading, setIsLoading] = useState(false);
  const hasSyncedUrlFilterRef = useRef(false);

  const fetchEtfs = useCallback(
    async ({
      ltpFilter,
      nextSortMode,
      nextPage,
      append,
    }: {
      ltpFilter: string;
      nextSortMode: BudgetEtfSortMode;
      nextPage: number;
      append: boolean;
    }) => {
      setIsLoading(true);
      try {
        const response = await getBudgetFriendlyETFs({
          page: nextPage,
          limit: PAGE_LIMIT,
          sort: resolveSortForApi(nextSortMode, defaultSort),
          ltpFilter,
        });

        const nextStocks = mapResponseToStocks(response);
        setStocks((prev) => (append ? [...prev, ...nextStocks] : nextStocks));
        setPage(nextPage);
        setShowMore(response?.showMore ?? false);
        setShowLess(response?.showLess ?? false);
      } catch (error) {
        console.error("Failed to fetch budget friendly ETFs:", error);
        if (!append) {
          setStocks([]);
          setShowMore(false);
          setShowLess(false);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [defaultSort],
  );

  useEffect(() => {
    if (hasSyncedUrlFilterRef.current) return;

    const urlFilter = resolveLtpFilter(
      searchParams.get(LTP_FILTER_QUERY_PARAM),
    );
    hasSyncedUrlFilterRef.current = true;

    if (urlFilter === resolveLtpFilter(initialLtpFilter)) {
      return;
    }

    setActiveFilter(urlFilter);
    void fetchEtfs({
      ltpFilter: urlFilter,
      nextSortMode: "default",
      nextPage: 1,
      append: false,
    });
  }, [fetchEtfs, initialLtpFilter, searchParams]);

  const updateUrlFilter = useCallback(
    (ltpFilter: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(LTP_FILTER_QUERY_PARAM, ltpFilter);
      router.replace(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams],
  );

  const handleFilterChange = (ltpFilter: string) => {
    if (!ltpFilter || ltpFilter === activeFilter) return;

    const filterIndex = BUDGET_ETF_FILTERS.findIndex(
      (filter) => filter?.value === ltpFilter,
    );
    void trackBudgetEtfClick({
      clickType: BUDGET_ETF_CLICK_TYPE_TAB,
      sectionTitle: heading,
      ctaText: BUDGET_ETF_FILTERS[filterIndex]?.label ?? ltpFilter,
      componentPosition,
      tabPosition: String(filterIndex + 1),
    });

    setActiveFilter(ltpFilter);
    setSortMode("default");
    updateUrlFilter(ltpFilter);
    void fetchEtfs({
      ltpFilter,
      nextSortMode: "default",
      nextPage: 1,
      append: false,
    });
  };

  const handleSortToggle = () => {
    void trackBudgetEtfClick({
      clickType: BUDGET_ETF_CLICK_TYPE_ICON,
      sectionTitle: heading,
      ctaText: "LTP",
      componentPosition,
    });

    const nextSortMode = getNextSortMode(sortMode);
    setSortMode(nextSortMode);
    void fetchEtfs({
      ltpFilter: activeFilter,
      nextSortMode,
      nextPage: 1,
      append: false,
    });
  };

  const handleToggleList = async () => {
    void trackBudgetEtfClick({
      clickType: BUDGET_ETF_CLICK_TYPE_LINK,
      sectionTitle: heading,
      ctaText: showMore ? showMoreText : showLessText,
      componentPosition,
    });

    if (showMore) {
      if (isLoading) return;

      await fetchEtfs({
        ltpFilter: activeFilter,
        nextSortMode: sortMode,
        nextPage: page + 1,
        append: true,
      });
      return;
    }

    if (showLess) {
      setSortMode("default");
      await fetchEtfs({
        ltpFilter: activeFilter,
        nextSortMode: "default",
        nextPage: 1,
        append: false,
      });
    }
  };

  const handleRowClick = useCallback(
    (stock: StockItem) => {
      void trackBudgetEtfClick({
        clickType: BUDGET_ETF_CLICK_TYPE_CARD,
        sectionTitle: heading,
        ctaText: stock.symbol,
        componentPosition,
      });

      void handleBudgetEtfCardClick(stock, {
        screen,
        loggedInRedirectionUrl,
        webViewTitle,
      });
    },
    [componentPosition, heading, loggedInRedirectionUrl, screen, webViewTitle],
  );

  if (!isLoading && stocks.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <BudgetEtfHeader header={heading} description={description} />
        <StockListNotFound
          errorMessage={stockNotFoundText}
          stockNotFoundImgUrl={stockNotFoundImgUrl}
        />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="px-4 pt-3">
        <BudgetEtfHeader header={heading} description={description} />
      </div>
      <div
        className={`border-2 border-b ${BUDGET_ETF_COLORS.border} lg:hidden`}
      ></div>
      <div className="flex flex-wrap gap-2 px-4">
        {BUDGET_ETF_FILTERS.map((filter) => {
          if (!filter?.value) return null;

          const isActive = filter.value === activeFilter;
          return (
            <button
              key={filter.value}
              type="button"
              onClick={() => handleFilterChange(filter.value)}
              className={`cursor-pointer rounded-[6px] border px-2 py-2 text-[12px] font-medium md:text-[14px] ${
                isActive
                  ? `${BUDGET_ETF_COLORS.activeTabBorder} ${BUDGET_ETF_COLORS.activeTabBg} ${BUDGET_ETF_COLORS.white}`
                  : `${BUDGET_ETF_COLORS.activeTabBorder} ${BUDGET_ETF_COLORS.whiteBg} ${BUDGET_ETF_COLORS.inactiveTabText}`
              }`}
            >
              {filter?.label ?? filter.value}
            </button>
          );
        })}
      </div>

      <div className={`border-2 border-b ${BUDGET_ETF_COLORS.border}`}></div>

      <div className="max-w-[892px] px-4">
        <div
          className={`grid grid-cols-[1fr_auto] border-b pb-2 text-[14px] ${BUDGET_ETF_COLORS.border} ${BUDGET_ETF_COLORS.primaryText}`}
        >
          <span className="hidden max-[439px]:inline">{mainHeading}</span>
          <span className="inline max-[439px]:hidden">{companyName}</span>
          <button
            type="button"
            onClick={handleSortToggle}
            className={`flex items-center gap-1 text-[12px] font-normal text-gray-600 sm:text-[14px]`}
            aria-label="Sort by LTP"
          >
            <span>LTP</span>
            <span className="flex flex-col items-center">
              <i
                className={`icomoon text-[10px] ${BUDGET_ETF_COLORS.linkPrimary} ${BUDGET_ETF_ICONS.trendUp}`}
              />
              <i
                className={`icomoon text-[10px] ${BUDGET_ETF_COLORS.linkPrimary} ${BUDGET_ETF_ICONS.trendDown}`}
              />
            </span>
          </button>
        </div>

        {stocks.map((stock) => (
          <BudgetEtfListRow
            key={`${stock.symbol}-${stock.token}`}
            stock={stock}
            isRowClickable={isRowClickable}
            onClick={isRowClickable ? handleRowClick : undefined}
          />
        ))}

        {isLoading ? (
          <div className="flex flex-col gap-3 py-3">
            {SKELETON_ROW_KEYS.map((skeletonKey) => (
              <BFLSkeleton
                key={skeletonKey}
                height={56}
                classes="w-full rounded-[8px]"
              />
            ))}
          </div>
        ) : null}

        {(showMore || showLess) && (
          <div className="flex justify-center py-3">
            <button
              type="button"
              onClick={() => void handleToggleList()}
              disabled={showMore && isLoading}
              className={`flex h-[40px] w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-[23px] border px-4 pb-[11px] pt-[12px] text-[14px] font-medium md:w-[258px] md:p-[12px_24px_11px_24px] ${BUDGET_ETF_COLORS.borderLinkPrimary} ${BUDGET_ETF_COLORS.linkPrimary} ${BUDGET_ETF_COLORS.hoverLinkPrimary} lg:hover:text-white ${
                showMore && isLoading ? "cursor-not-allowed opacity-60" : ""
              }`}
            >
              {showMore ? showMoreText : showLessText}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetETFMainClient;
